/**
 * Shared utilities for metric display logic
 * Used by both AddNewContentModal and DashboardMetricCard to ensure consistency
 */

import moment from 'moment';
import { 
  getMetricCustomName,
  METRIC_SPECIAL_COMPARISON_LABELS,
  type GranularityType 
} from '../config';

export interface MetricTextInfo {
  value: string;
  text_en: string;
  text_cn: string;
  formatter?: string;
  unit?: string;
  explainEn?: string;
  explainCn?: string;
  granularity?: string;
}

export interface DateRange {
  start_time: string;
  end_time: string;
}

/**
 * Transform metric key to Rainbow Info key based on granularity
 * Only active_users needs transformation: active_users → active_users_dau/wau/mau
 */
export const transformToRainbowKey = (metricKey: string, granularity: string): string => {
  if (metricKey === 'active_users') {
    if (granularity === 'daily') {
      return 'active_users_dau';
    } else if (granularity === 'weekly') {
      return 'active_users_wau';
    } else if (granularity === 'monthly') {
      return 'active_users_mau';
    }
  }
  return metricKey;
};

/**
 * Fetch and process Rainbow Info for metrics
 * Handles the special active_users transformation and returns processed metric text info
 * 
 * @param rainbowInfoResponse - Raw response from getDashboardRainbowInfo API
 * @param metricsByGranularity - Object mapping granularity to array of metric keys
 * @returns Array of MetricTextInfo with granularity context
 */
export const processRainbowInfo = (
  rainbowInfoResponse: any[],
  metricsByGranularity: Record<string, string[]>
): MetricTextInfo[] => {
  if (!Array.isArray(rainbowInfoResponse)) {
    return [];
  }

  // Build mapping of metric key with granularity suffix for Rainbow Info matching
  const allMetricsWithGranularity: Array<{ originalKey: string; rainbowKey: string; granularity: string }> = [];
  
  Object.entries(metricsByGranularity).forEach(([granularity, metrics]) => {
    metrics.forEach(metricKey => {
      const rainbowKey = transformToRainbowKey(metricKey, granularity);
      allMetricsWithGranularity.push({ originalKey: metricKey, rainbowKey, granularity });
    });
  });

  // Get all rainbow keys for matching
  const allRainbowKeys = allMetricsWithGranularity.map(m => m.rainbowKey);
  
  // Filter rows to match using rainbow keys (e.g., active_users_dau, active_users_wau)
  const matchedRows = rainbowInfoResponse.filter((row: any) => 
    allRainbowKeys.includes(row.key)
  );

  // Create a reverse map: rainbowKey → { originalKey, granularity }
  const rainbowToOriginalMap: Record<string, { originalKey: string; granularity: string }> = {};
  allMetricsWithGranularity.forEach(({ originalKey, rainbowKey, granularity }) => {
    rainbowToOriginalMap[rainbowKey] = { originalKey, granularity };
  });

  // Process matched rows
  const processedTextInfo = matchedRows.map((row: any) => {
    try {
      const parsedValue = JSON.parse(row.value);
      // Map rainbow key back to original key
      const mappedData = rainbowToOriginalMap[row.key] || { originalKey: row.key, granularity: undefined };
      
      return {
        value: mappedData.originalKey, // Use ORIGINAL metric key (e.g., active_users, not active_users_dau)
        text_en: parsedValue.en || '',
        text_cn: parsedValue.cn || '',
        formatter: parsedValue.formatter || '',
        unit: parsedValue.unit || undefined,
        explainEn: parsedValue.explainEn || undefined,
        explainCn: parsedValue.explainCn || undefined,
        granularity: mappedData.granularity, // Store granularity to distinguish active_users variants
      };
    } catch (parseError) {
      const mappedData = rainbowToOriginalMap[row.key] || { originalKey: row.key, granularity: undefined };
      console.error(`Error parsing JSON for rainbow key ${row.key} (original: ${mappedData.originalKey}):`, parseError);
      return {
        value: mappedData.originalKey,
        text_en: '',
        text_cn: '',
        formatter: '',
        unit: undefined,
        explainEn: undefined,
        explainCn: undefined,
        granularity: mappedData.granularity,
      };
    }
  });

  return processedTextInfo;
};

/**
 * Get metric display name with customization logic
 * 
 * Simple 2-step priority:
 * 1. Check for custom name (METRIC_CUSTOM_NAMES) → return if exists
 * 2. Use Rainbow API name → return as-is
 * 
 * This gives you complete control over naming. Add any metric to METRIC_CUSTOM_NAMES
 * if you want to override its name, otherwise it will use the Rainbow API name.
 * 
 * @param metricKey - The metric key
 * @param locale - Current locale ('en-US' or 'zh-CN')
 * @param metricTextInfo - Metric text info from Rainbow API
 * @param granularity - Optional granularity for context-specific names
 * @returns Display name for the metric
 */
export const getMetricDisplayName = (
  metricKey: string,
  locale: string,
  metricTextInfo: MetricTextInfo[],
  granularity?: string
): string => {
  const isZh = locale === 'zh-CN';

  // Step 1: Check for custom names first
  if (granularity) {
    const customName = getMetricCustomName(
      metricKey,
      granularity as GranularityType,
      locale
    );
    if (customName) {
      return customName; // Return custom name directly
    }
  }

  // Step 2: Get name from Rainbow API
  let metric;
  if (metricKey === 'active_users' && granularity) {
    metric = metricTextInfo.find(item => item.value === metricKey && item.granularity === granularity);
  } else {
    metric = metricTextInfo.find(item => item.value === metricKey);
  }
  
  if (metric) {
    // return isZh ? metric.text_cn : metric.text_en;
    return metric.text_en; // temporary disable chinese name
  }
  
  // Fallback to the key itself if not found
  return metricKey;
};

/**
 * Find metric info from Rainbow Info API data
 * For active_users, matches by both metric key AND granularity
 * For other metrics, matches by metric key only
 */
export const findMetricInfo = (
  metricKey: string,
  metricTextInfo: MetricTextInfo[],
  granularity?: string
): MetricTextInfo | undefined => {
  if (metricKey === 'active_users' && granularity) {
    return metricTextInfo.find(item => item.value === metricKey && item.granularity === granularity);
  } else {
    return metricTextInfo.find(item => item.value === metricKey);
  }
};

/**
 * Get comparison labels based on granularity
 * Returns appropriate labels for DoD/WoW/MoM and SDLW/SWLM/SMLY
 * Supports special metrics with custom single-value comparison labels
 * 
 * @param granularity - 'daily', 'weekly', or 'monthly'
 * @param dodValue - First comparison value (DoD/WoW/MoM or single change value)
 * @param sdlwValue - Second comparison value (SDLW/SWLM/SMLY, not used for special metrics)
 * @param formatPercentageFn - Function to format percentage values
 * @param metricKey - Optional metric key to check for special comparison behavior
 * @returns Array of comparison label objects
 */
export const getComparisonLabels = (
  granularity: 'daily' | 'weekly' | 'monthly',
  dodValue: number | string | null | undefined,
  sdlwValue: number | string | null | undefined,
  formatPercentageFn: (value: string | null | undefined) => string,
  metricKey?: string
): Array<{ value: string; text: string }> => {
  // Check if metric has special comparison label
  if (metricKey && METRIC_SPECIAL_COMPARISON_LABELS[metricKey]) {
    const dodString = typeof dodValue === 'number' ? String(dodValue) : dodValue;
    return [
      { 
        value: METRIC_SPECIAL_COMPARISON_LABELS[metricKey], 
        text: formatPercentageFn(dodString) 
      }
    ];
  }

  // Standard comparison labels based on granularity
  const dodString = typeof dodValue === 'number' ? String(dodValue) : dodValue;
  const sdlwString = typeof sdlwValue === 'number' ? String(sdlwValue) : sdlwValue;
  
  if (granularity === 'daily') {
    return [
      { value: 'DoD', text: formatPercentageFn(dodString) },
      { value: 'SDLW', text: formatPercentageFn(sdlwString) }
    ];
  } else if (granularity === 'weekly') {
    return [
      { value: 'WoW', text: formatPercentageFn(dodString) },
      { value: 'SWLM', text: formatPercentageFn(sdlwString) }
    ];
  } else if (granularity === 'monthly') {
    return [
      { value: 'MoM', text: formatPercentageFn(dodString) },
      { value: 'SMLY', text: formatPercentageFn(sdlwString) }
    ];
  }
  
  // Default fallback
  return [];
};

/**
 * Filter metrics to only include those with Rainbow Info
 * For active_users, checks if there's an entry with matching granularity
 */
export const filterMetricsWithRainbowInfo = (
  metrics: string[],
  metricTextInfo: MetricTextInfo[],
  granularity: string
): string[] => {
  return metrics.filter(metricKey => {
    if (metricKey === 'active_users') {
      return metricTextInfo.some(item => item.value === metricKey && item.granularity === granularity);
    } else {
      return metricTextInfo.some(item => item.value === metricKey);
    }
  });
};

/**
 * Check if current time (UTC+8) is before 5 PM
 * Used to determine which date range to use for data requests
 */
export const isBefore5PM = (): boolean => {
  const nowUTC8 = moment().utcOffset(8);
  const currentHour = nowUTC8.hour();
  return currentHour < 17;
};

/**
 * Calculate date range for daily metrics
 * Before 5 PM: T-15 to T-2
 * After 5 PM: T-14 to T-1
 */
export const getDailyDateRange = (): DateRange => {
  const isBefore5 = isBefore5PM();
  
  const end_time = isBefore5
    ? moment().subtract(2, 'days').format('YYYY-MM-DD')  // T-2
    : moment().subtract(1, 'day').format('YYYY-MM-DD');  // T-1

  const start_time = isBefore5
    ? moment().subtract(15, 'days').format('YYYY-MM-DD')  // T-15
    : moment().subtract(14, 'days').format('YYYY-MM-DD'); // T-14

  return { start_time, end_time };
};

/**
 * Calculate date range for weekly metrics
 * Start: Monday of 6 weeks ago
 * End: Sunday of last week
 */
export const getWeeklyDateRange = (): DateRange => {
  // Get Monday of 6 weeks ago
  const start_time = moment().subtract(6, 'weeks').day(1).format('YYYY-MM-DD');
  
  // Get Sunday of last week (1 week ago)
  const end_time = moment().subtract(1, 'week').day(7).format('YYYY-MM-DD');

  return { start_time, end_time };
};

/**
 * Calculate date range for monthly metrics
 * Start: First day of 6 months ago
 * End: Last day of last month
 */
export const getMonthlyDateRange = (): DateRange => {
  // Get first day of 6 months ago
  const start_time = moment().subtract(6, 'months').startOf('month').format('YYYY-MM-DD');
  
  // Get last day of last month
  const end_time = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

  return { start_time, end_time };
};

/**
 * Get date range based on granularity
 * Convenience wrapper for getDailyDateRange, getWeeklyDateRange, getMonthlyDateRange
 */
export const getDateRangeByGranularity = (granularity: 'daily' | 'weekly' | 'monthly'): DateRange => {
  switch (granularity) {
    case 'daily':
      return getDailyDateRange();
    case 'weekly':
      return getWeeklyDateRange();
    case 'monthly':
      return getMonthlyDateRange();
    default:
      return getDailyDateRange(); // Default to daily
  }
};
